import { GameCheatsheet } from '../components/GameCheatsheet';
import { BridgeDealDriver } from '../lib/BridgeDealDriver';
import type { BridgeNtState } from '../games/bridge-nt';
/**
 * Play 画面: タイトルバー + ターンバナー + アクティブ盤 + ミニ盤タブ + クロック。
 *
 * - 終局時 (phase==='finished') は Result 画面が呼ばれるので、ここでは playing のみ前提。
 * - 直前の一手は `boards[i].history.at(-1).mv` を盤コンポーネントに渡してリング表示。
 * - ハンバーガーメニューに 投了 / 設定 / 退出 を集約。
 */

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BoardToolsScene } from '../components/BoardToolsScene';
import { BoardTurnStatus } from '../components/BoardTurnStatus';
import { BoardObjective } from '../components/BoardObjective';
import { BoardRenderer } from '../components/board/BoardRenderer';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { Icon, type IconName } from '../components/Icon';
import { LoadingScreen } from '../components/Loading';
import { MatchClock } from '../components/MatchClock';
import { TitleBar } from '../components/TitleBar';
import { WasmError } from '../components/WasmError';
import type { Side } from '../games/animal';
import type { PokerMatchState, Seat } from '../games/heads-up-poker';
import { useBackgammonVrf } from '../hooks/useBackgammonVrf';
import { useLegalMoves } from '../hooks/useLegalMoves';
import { useMoveSound } from '../hooks/useMoveSound';
import { unlockAudio } from '../lib/audio';
import { gameLabel, gameShort, NEXT_SEVEN_GAME_ORDER } from '../lib/game-meta';
import { MentalPokerDriver } from '../lib/MentalPokerDriver';
import {
  allowedBoardsFor,
  type BoardEntry,
  mySeatOf,
  myTurnAllowedOn,
  nextBoardForSeat,
  opponentOf,
  type Snapshot,
  sideOnBoard,
  slotSideOnBoard,
} from '../lib/match-snapshot';
import { navigate } from '../lib/navigate';
import { useUiBusyAnim } from '../lib/ui-busy';
import { useWasmReady } from '../lib/useWasmReady';
import { usePrefs } from '../state/prefs';
import { useRoom } from '../state/room';
import type { GameId } from '../wasm';

// busy 管理で snapshot が変わっていれば busy を空扱いするための共有 sentinel。
// 毎 render で新規 Set を作らないように module-scope 不変参照。
const EMPTY_BOARD_SET: ReadonlySet<number> = new Set();

export function Play({ code }: { code: string }) {
  const snapshot = useRoom((s) => s.snapshot) as Snapshot | null;
  const you = useRoom((s) => s.you);
  const participants = useRoom((s) => s.participants);
  const config = useRoom((s) => s.config);
  const sendMove = useRoom((s) => s.sendMove);
  const resign = useRoom((s) => s.resign);
  const isLocal = useRoom((s) => s.isLocal);
  const offerDraw = useRoom((s) => s.offerDraw);
  const acceptDraw = useRoom((s) => s.acceptDraw);
  const pendingDraw = useRoom((s) => s.pendingDraw);
  const phase = useRoom((s) => s.phase);
  const lastError = useRoom((s) => s.lastError);
  const pendingMoves = useRoom((s) => s.pending);
  // §8.10.A 駆動要件: HU Poker 盤の MentalPokerDriver 常駐マウント判定で使う
  // mps store。 hook の呼び出し位置は「常に component 冒頭で固定」 が Rules of Hooks
  // 要件 — snapshot 未着時の早期 return より下に置くと render ごとに hook 数が
  // 変動して `Rendered more hooks than during the previous render` で落ちる。
  const mpsResults = useRoom((s) => s.mps);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inspectedBoard = useRef<{ code: string; index: number } | null>(null);
  const { wasmReady, wasmError, retryWasm } = useWasmReady();
  // Saved matches retain their engine board order and IDs. Reorder only the new
  // games' visual slots, so an old ongoing match also uses the current tab order.
  const tabOrderKey = snapshot?.boards.map(entry => entry.game_id).join(',') ?? '';
  const tabIndices = useMemo(() => {
    const games = tabOrderKey ? tabOrderKey.split(',') as GameId[] : [];
    const newIndices = games.map((_, index) => index).filter(index => NEXT_SEVEN_GAME_ORDER.includes(games[index]))
      .sort((a, b) => NEXT_SEVEN_GAME_ORDER.indexOf(games[a]) - NEXT_SEVEN_GAME_ORDER.indexOf(games[b]));
    let next = 0;
    return games.map((game, index) => NEXT_SEVEN_GAME_ORDER.includes(game) ? newIndices[next++] : index);
  }, [tabOrderKey]);
  const visualIndex = (index: number) => tabIndices.indexOf(index);

  // active 切替時のスライド方向。 setActiveAnimated 経由で active と一緒に同一バッチで
  // 更新するので 「新盤が古い slideDir で mount → animation 走ってから clear」 の
  // ちらつきが起きない。 passive: true (= 相手の応手待ち画面への受動的な移動) では
  // null をセットして animation を出さない。
  const [slideDir, setSlideDir] = useState<'right' | 'left' | null>(null);
  const setActiveAnimated = (target: number, options: { passive?: boolean } = {}) => {
    const entry = snapshot?.boards[target];
    inspectedBoard.current = entry && NEXT_SEVEN_GAME_ORDER.includes(entry.game_id)
      ? { code, index: target } : null;
    setActive((cur) => {
      if (cur === target) return cur;
      if (options.passive) {
        setSlideDir(null);
      } else {
        setSlideDir(visualIndex(target) > visualIndex(cur) ? 'right' : 'left');
      }
      return target;
    });
  };

  /** clock scope: "Whole" (= マッチ全体 1 個) or "PerBoard" (= 各盤独立)。 docs/CLOCK.md §1〜§2。 */
  const clockScope =
    ((config?.clock as { scope?: string } | undefined)?.scope ?? 'Whole');
  const isWholePolicy = clockScope !== 'PerBoard';


  const hostName =
    participants.find((p) => p.role === 'host')?.display_name ?? 'Host';
  const guestName =
    participants.find((p) => p.role === 'guest')?.display_name ?? 'Guest';
  const yourName = you === 'host' ? hostName : you === 'guest' ? guestName : null;
  const oppName = you === 'host' ? guestName : you === 'guest' ? hostName : null;
  const boards = snapshot?.boards ?? [];

  // 「直前に送った着手の server ack 待ち」 状態を盤ごとに記録 (= 同盤の二重 click 抑止)。
  // ・AnimalShogi / Reversi など 1 move = 1 ターンのゲームでは、 React の re-render が走る
  //   前のサブ ms ウィンドウで連打されると同じ手を二度送ってしまう。
  // ・Backgammon: 1 ターンに複数着手するので gate せず、 engine の current_side に任せる。
  //
  // **報告された 「2 手目が指せない (リロードで治る)」 バグの原因**:
  //   旧実装は busy を useState<Set> で管理し、 send 時に functional updater、 snapshot 変更
  //   検知時 (render 中) に value setter で reset していた。 React 18 + StrictMode では
  //   functional updater が二重実行され、 「render 中の value reset」 と batch されると
  //   busy がクリアされた直後に再び `{0}` に戻る (= 二重実行された updater の 2 回目が
  //   後勝ちする) という stuck 状態を起こす。 結果として 「自分の 2 手目を click しても
  //   busy guard でドロップされ、 着手が送信されない」 となり、 リロード (= Play unmount
  //   再 mount で busyState 初期化) しか復旧手段が無くなる。
  //
  // **修正**: busy を 「どの snapshot 時点での busy か」 とセットで ref に保持する。
  //   - send 時に busyRef.current = { snap: 現 snapshot, boards: 該当 board }。
  //   - click 受信時、 busyRef.current.snap === 現 snapshot かつ boards に含まれるなら drop。
  //   - snapshot が新しくなれば busyRef.current.snap !== 現 snapshot で自動 stale 化、
  //     setState を介さず render-side で busy が消える (= setState 競合が発生しない)。
  //   useRef を使うのは、 同一 render 内の double-click を検出するには「click 直後に
  //   即座に busy が立つ」 必要があり、 useState では setState の batch まで反映されない
  //   ため。 ref なら同期で書き換わり、 次の click 判定で読み取れる。
  const busyRef = useRef<{ snap: typeof snapshot; boards: Set<number>; clients: Map<number,string> }>({
    snap: null,
    boards: new Set(),
    clients: new Map(),
  });
  // Invalid Kyoen claims are ordinary user input. A rejection does not change the
  // board snapshot, so release the guard when that exact pending request is gone.
  useEffect(() => {
    for (const [board, id] of busyRef.current.clients) {
      if (!pendingMoves.has(id)) {
        busyRef.current.boards.delete(board);
        busyRef.current.clients.delete(board);
      }
    }
  }, [pendingMoves]);
  const busyBoards: ReadonlySet<number> =
    busyRef.current.snap === snapshot ? busyRef.current.boards : EMPTY_BOARD_SET;
  const sendMoveGated = (board: number, mv: unknown) => {
    if (busyBoards.has(board)) return; // 同じ盤の二重 click 抑止 (= 連打防止)
    const entry = boards.find((b) => b.board === board);
    const gameId = entry?.game_id;
    // BG は多手着手を許す → gate しない。 他は gate。
    if (gameId !== 'Backgammon') {
      // busyRef は同期で書き換える。 ref なので React state ではなく、 useState の
      // batch 処理 / StrictMode 二重実行に巻き込まれない。 次 render で snapshot が
      // 変われば自動的に stale 化する (= clear 不要)。
      const prev =
        busyRef.current.snap === snapshot ? busyRef.current.boards : new Set<number>();
      const next = new Set(prev);
      next.add(board);
      busyRef.current = { snap: snapshot, boards: next, clients: new Map(busyRef.current.snap === snapshot ? busyRef.current.clients : []) };
    }
    inspectedBoard.current = null;
    const id = sendMove(board, mv);
    if (gameId !== 'Backgammon') busyRef.current.clients.set(board,id);
  };

  /** マッチ全体での自分の MatchSeat。 観戦者なら null。 */
  const mySeat = useMemo(() => mySeatOf(snapshot, you), [snapshot, you]);
  const opponentSeat = useMemo(() => (mySeat ? opponentOf(mySeat) : null), [mySeat]);

  // 効果音: snapshot の history 増分を検出して着手音を鳴らす (自分・相手・CPU 共通)。
  useMoveSound(snapshot);
  // user gesture (= 初回 click) で AudioContext を「起こす」 (iOS Safari 制限対応)。
  useEffect(() => {
    const handler = () => { void unlockAudio(); };
    window.addEventListener('pointerdown', handler, { once: true });
    return () => window.removeEventListener('pointerdown', handler);
  }, []);
  // Play 画面のメニューに効果音 ON/OFF トグルを出すための pref subscribe。
  const soundEnabled = usePrefs((s) => s.soundEnabled);
  const setSound = usePrefs((s) => s.setSound);

  /** board ごとに自分が打つ per-board **物理色** (Renju swap parity 込み)。
   *  盤描画用 (= 駒色を反映)。 勝敗 / 手番判定には `slotForBoard` を使う。 */
  const sideForBoard = useCallback(
    (boardIdx: number): Side | null => sideOnBoard(snapshot, mySeat, boardIdx),
    [snapshot, mySeat],
  );
  /** board ごとの自分の **slot Side** (= first_seat ベース、 Swap でも不変)。
   *  `b.result` / `b.current_side` / `b.history[].side` (= 全て slot) との比較に使う。 */
  const slotForBoard = useCallback(
    (boardIdx: number): Side | null => slotSideOnBoard(snapshot, mySeat, boardIdx),
    [snapshot, mySeat],
  );

  /** サーバ派生 `_view.allowed_boards[mySeat]` に当該 board が含まれているか
   *  (= 「今この瞬間に指せるか」)。 docs/ORCHESTRATOR.md §3-5。 */
  const myTurnAllowedFor = useCallback(
    (boardIdx: number): boolean => myTurnAllowedOn(snapshot, mySeat, boardIdx),
    [snapshot, mySeat],
  );

  // 合法手は active 盤しか render に使わないので、 active 盤だけ計算する。
  // 7 盤 mixed で他 6 盤分の WASM legal を毎 snapshot 走らせるのを止めて、 board 切替時にだけ
  // 新しい active 盤の legal を計算する形にする。 (中身のキャッシュは useLegalMoves 側で content
  // ハッシュで持つので、 他盤の sub-move で snapshot ref だけ変わる場合は WASM スキップ。)
  const legalSourceBoards = useMemo<BoardEntry[]>(() => {
    const b = boards[active];
    return b ? [b] : [];
  }, [boards, active]);
  const legalByBoard = useLegalMoves(legalSourceBoards, wasmReady);

  // board_id → array index 変換テーブル (= boards[idxByBoardId[id]].board === id)。
  // 通常 boards[i].board == i だが、 将来 board 削除等の余地を残すため明示マッピング。
  const idxByBoardId = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    boards.forEach((b, i) => {
      map[b.board] = i;
    });
    return map;
  }, [boards]);

  // 自分が指せる board の index 一覧 (board_id 昇順)。 直列 / 並列では高々 1 つだが、
  // 各盤独立 (FullyParallel) では複数が同時に立つ。
  const myTurnBoardIdxs = useMemo<number[]>(() => {
    const out: number[] = [];
    for (const id of allowedBoardsFor(snapshot, mySeat)) {
      const idx = idxByBoardId[id];
      if (idx != null) out.push(idx);
    }
    return out;
  }, [snapshot, mySeat, idxByBoardId]);
  // 自動切替に使う primitive: 「自分が指せる leftmost board の index」 と「相手の next_board index」。
  // 両方とも turn-end でしか動かない (BG sub-move では allowed_boards も pointer も不変)。
  const myTurnBoardIdx = myTurnBoardIdxs[0] ?? -1;
  // effect の dep は primitive に保ちたいので、 集合そのものは ref 越しに読む。
  const myTurnBoardKey = myTurnBoardIdxs.join(',');
  const myTurnBoardIdxsRef = useRef(myTurnBoardIdxs);
  myTurnBoardIdxsRef.current = myTurnBoardIdxs;

  const oppNextBoardIdx = useMemo<number>(() => {
    const id = nextBoardForSeat(snapshot, opponentSeat);
    if (id == null) return -1;
    return idxByBoardId[id] ?? -1;
  }, [snapshot, opponentSeat, idxByBoardId]);

  // 盤自動切替 ([docs/ORCHESTRATOR.md] §6):
  //   ⓪ **今いる盤が自分で指せるなら動かさない**
  //   ① 自分の手番が立つ board (leftmost) へスライド
  //   ② どこにも自分の手番が無い → 相手の next_board へ受動追従
  //
  // ⓪ が要る理由: この effect は相手の着手でも再発火する (oppNextBoardIdx が動くため)。
  // 各盤独立では自分の手番盤が複数同時に立つので、 ⓪ が無いと **別の盤で入力している
  // 最中に leftmost へ引き戻される**。 盤は key で作り直されるので、 選択中の駒も
  // 仮確定もそこで消える。
  // (2026-09-19 対人戦で確認: 5 盤・各盤独立で host がチェスを表示中に guest が
  //  どうぶつを指すと、 host の表示が連珠へ飛んでいた)
  //
  // dep は全部 primitive (number / string / boolean)。
  // BG sub-move では myTurnBoardKey / oppNextBoardIdx いずれも不変なので
  // 自分のターン途中で勝手に他盤へ飛ぶことはない。
  const boardsLen = boards.length;
  const isPlayer = !!you && you !== 'spectator';
  // biome-ignore lint/correctness/useExhaustiveDependencies: myTurnBoardIdx は myTurnBoardKey から導かれるので dep に入れない (集合は ref で読む)。
  useEffect(() => {
    if (!isPlayer || boardsLen === 0 || cheatsheetOpen) return;
    const t = setTimeout(() => {
      setActive((cur) => {
        // A manually selected new board remains available for inspection of any
        // piece, including opponents. Sending a move resumes the usual following.
        if (inspectedBoard.current?.code === code && inspectedBoard.current.index === cur) return cur;
        if (myTurnBoardIdxsRef.current.includes(cur)) return cur;
        if (myTurnBoardIdx >= 0) {
          if (myTurnBoardIdx !== cur) setSlideDir(tabIndices.indexOf(myTurnBoardIdx) > tabIndices.indexOf(cur) ? 'right' : 'left');
          return myTurnBoardIdx;
        }
        if (oppNextBoardIdx >= 0) {
          if (oppNextBoardIdx !== cur) setSlideDir(null);
          return oppNextBoardIdx;
        }
        return cur;
      });
    }, 100);
    return () => clearTimeout(t);
  }, [myTurnBoardKey, oppNextBoardIdx, boardsLen, isPlayer, code, tabIndices, cheatsheetOpen]);

  // Backgammon の VRF 流し (公開鍵 publish + dice 空時の proof 提出)
  useBackgammonVrf({ boards, you, wasmReady });

  // 過半数判定 (per-board の Side 反転を反映)
  const losingMajority = (() => {
    if (!you || you === 'spectator') return false;
    let me = 0;
    let opp = 0;
    let remaining = 0;
    for (const b of boards) {
      // 勝敗判定は slot で。 物理色 (sideForBoard) は Renju Swap で result との意味論が
      // 乖離するため使えない。
      const myS = slotForBoard(b.board);
      if (!myS) continue;
      const oppS: Side = myS === 'First' ? 'Second' : 'First';
      if (b.result === myS) me++;
      else if (b.result === oppS) opp++;
      else if (b.result === null) remaining++;
    }
    return opp > me + remaining;
  })();

  // 対人戦で過半数の盤を落とした (= 残り全勝でも追いつかない) ら、 自動で全体投了する。
  // CPU 戦は CPU が指してくれず続行できないので、 ユーザー判断で残り盤を進める or
  // 結果画面に行く選択肢を残し、 hint だけ出す。
  // 多重発火防止に ref で 1 度だけ。
  const autoResignFiredRef = useRef(false);
  useEffect(() => {
    if (!losingMajority) return;
    if (isLocal) return;
    if (phase !== 'playing') return;
    if (autoResignFiredRef.current) return;
    autoResignFiredRef.current = true;
    resign();
  }, [losingMajority, isLocal, phase, resign]);

  const activeBoard = boards[active];
  // 互換のため、 active board における自分の Side を計算 (BackgammonBoard 等に渡す)
  const yourSide: Side | null = activeBoard ? sideForBoard(activeBoard.board) : null;

  if (wasmError) {
    return (
      <main className="centered">
        <WasmError onRetry={retryWasm} />
      </main>
    );
  }
  if (!snapshot || !activeBoard) {
    return <LoadingScreen label="対局を準備中" />;
  }
  const activeFinished = activeBoard.result !== null;
  // 終局した盤の勝敗は帯や文言ではなく、 名前の左の記号 (王冠 / 白旗 / 天秤) だけにする。
  // 勝敗は slot ベースで比較 (yourSide = 物理色 は Renju Swap で逆転する)。
  const activeOutcome: 'you' | 'opp' | 'draw' | null = (() => {
    if (!activeFinished) return null;
    const r = activeBoard.result;
    if (r === 'Draw') return 'draw';
    if (r == null || !you || you === 'spectator') return null;
    const yourSlot = slotForBoard(activeBoard.board);
    if (!yourSlot) return null;
    return r === yourSlot ? 'you' : 'opp';
  })();
  const lastMove = activeBoard.history.at(-1)?.mv;
  // Connect6 の 1 ターン 2 石ハイライト用 (他ゲームは未使用)。
  const prevMove = activeBoard.history.at(-2)?.mv;
  const sendBoardAction = useRoom.getState().sendBoardAction;
  // 引き分け提案の自他判定はマッチ全体一意な mySeat を使う。 yourSide は board ごとに
  // 反転するため、 そこから MatchSeat を導出すると first_seat 配置によって誤判定する。
  const pendingOnBoard = activeBoard.pending_draw_offer ?? null;
  const pendingFromOpponent = pendingOnBoard != null && mySeat != null && pendingOnBoard !== mySeat;
  const pendingFromMe = pendingOnBoard != null && mySeat != null && pendingOnBoard === mySeat;

  // §8.10.A 駆動要件: HU Poker 盤について MentalPokerDriver を常駐マウント (= mini/full、
  // アクティブタブ/非アクティブと独立に protocol を駆動)。 stop 条件は (a) hand 終了、
  // (b) board 終了、 (c) spectator、 (d) cheatDetected (= Play 階層で mps store を read)。
  // mpsResults は component 冒頭で取得済 (= Rules of Hooks 順序固定のため)。
  const pokerDrivers = boards
    .filter((b) => b.game_id === 'HeadsUpPoker' && b.result === null)
    .map((b) => {
      const st = b.state as PokerMatchState;
      const yourBoardSide = sideForBoard(b.board);
      // dealer_side と一致する側が SB (= HeadsUpPokerBoard の sideToSeat と同一規約)。
      const seat: Seat | null =
        yourBoardSide == null
          ? null
          : yourBoardSide === st.current_dealer_side
            ? 'sb'
            : 'bb';
      const phase = st.current_hand.phase;
      // mid-hand リロード時の short-circuit 用 engine 値 (= 既に provide 済の hole/board)。
      // seat が null (= spectator) なら hole は null 扱い。
      const seatIdx = seat === 'sb' ? 0 : seat === 'bb' ? 1 : -1;
      const engineMyHole =
        seatIdx >= 0 ? (st.current_hand.hole[seatIdx] ?? null) : null;
      const engineBoard = st.current_hand.board ?? [];
      const handNum = st.current_hand.hand_number;
      // §8.10.A (d): cheatDetected 時は protocol 信頼不能。 駆動を止めて wasm の verify 無駄走を
      // 抑止する (= player は HeadsUpPokerBoard の cheat banner を見て「Fold (Failed Recover)」 で
      // hand を諦める手動 path に乗る、 §1.9)。 awaiting_cards のまま永久 stuck することは無い。
      const cheatDetected = mpsResults.get(`${b.board}:${handNum}`)?.cheatDetected ?? false;
      return {
        board: b.board,
        hand: handNum,
        role: you,
        mySeat: seat,
        awaiting: st.current_hand.awaiting_cards ?? null,
        handPhase: phase,
        matchCode: code,
        engineMyHole,
        engineBoard,
        // auto-provide 用 (= 並列モードで他盤タブから戻らなくても provide される):
        myTurnAllowed: myTurnAllowedFor(b.board),
        toAct: st.current_hand.to_act ?? null,
        boardLen: engineBoard.length,
        // (a) hand_complete / match_complete、 (c) spectator、 (d) cheatDetected では stop。
        active:
          you !== 'spectator' &&
          phase !== 'hand_complete' &&
          phase !== 'match_complete' &&
          !cheatDetected,
      };
    });

  return (
    // 盤が 1 つのときはタブのミニ盤が完全に冗長 (すぐ上に同じ盤がある)。 畳んで
    // 縦を盤に回す。 縦 budget も CSS 側でこのクラスに合わせて広げる。
    <main className={`play-shell ${boards.length === 1 ? 'single-board' : ''}`}>
      {boards.filter(b => b.game_id === 'BridgeNT' && b.result === null).map(b => <BridgeDealDriver key={`bridge-deal-${code}-${b.board}`} board={b.board} state={b.state as BridgeNtState} side={sideForBoard(b.board)} role={you} matchCode={code} myTurnAllowed={myTurnAllowedFor(b.board)} />)}
      {pokerDrivers.map((p) => (
        <MentalPokerDriver key={`mps-${code}-${p.board}`} {...p} />
      ))}
      <TitleBar
        center={
          <div className="play-title-center">
            {/* 名前と残時間は盤の上下に置くので、 ここはゲーム名だけ。 */}
            <span className="play-title-game">{gameLabel(activeBoard.game_id)}</span>
            <ConnectionBadge />
          </div>
        }
        menu={[
          ...(phase === 'playing' && yourSide && !activeFinished
            ? [{
                label: 'この盤を投了',
                variant: 'danger' as const,
                onClick: () => {
                  if (window.confirm('この盤を投了しますか?')) {
                    sendBoardAction(activeBoard.board, 'Resign');
                  }
                },
                testId: `board-resign-${activeBoard.board}`,
              }]
            : []),
          ...(phase === 'playing' && yourSide && !activeFinished && !pendingFromMe
            ? [{
                label: 'この盤に引き分け提案',
                onClick: () => sendBoardAction(activeBoard.board, 'OfferDraw'),
                testId: `board-offer-draw-${activeBoard.board}`,
              }]
            : []),
          ...(phase === 'playing' && yourSide && pendingFromMe
            ? [{
                label: '引き分け提案を取り下げ',
                onClick: () => sendBoardAction(activeBoard.board, 'DeclineDraw'),
              }]
            : []),
          ...(phase === 'playing' && yourSide
            ? [{
                label: 'マッチ全体を投了',
                variant: 'danger' as const,
                onClick: () => resign(),
                testId: 'resign',
              }]
            : []),
          ...(phase === 'playing' && yourSide && !pendingDraw
            ? [{
                label: 'マッチに引き分け申し出',
                onClick: () => offerDraw(),
                testId: 'offer-draw',
              }]
            : []),
          {
            label: soundEnabled ? '効果音: ON' : '効果音: OFF',
            onClick: () => {
              setSound(!soundEnabled);
              // ON にしたタイミングが「初回 user gesture」 なら AudioContext を起こす。
              if (!soundEnabled) void unlockAudio();
            },
            testId: 'toggle-sound',
          },
          { label: '設定', onClick: () => navigate('/settings') },
          {
            label: '部屋を出る',
            variant: 'danger' as const,
            onClick: () => navigate('/'),
          },
        ]}
      />

      {pendingDraw && yourSide && (
        <DrawOfferBanner
          from={pendingDraw}
          you={yourSide}
          onAccept={acceptDraw}
        />
      )}

      {/* turn-strip は冗長なので撤去。 各タブの tab-turn-dot で自分番判別可能。 */}

      {/* 目的文: アクティブ盤の「何をすれば勝ちか」 を 1 行だけ盤の外・上に出す。 */}
      <div className="cheat-objective-row"><GameCheatsheet key={activeBoard.game_id} game={activeBoard.game_id} onOpenChange={setCheatsheetOpen} description={<BoardObjective game={activeBoard.game_id} concise={NEXT_SEVEN_GAME_ORDER.includes(activeBoard.game_id)} />} /></div>

      {/* 盤の上 = 相手、 盤の下 = 自分。 進行モードによらず同じ 1 行で、
          複数時計並列のときは「いま見ている盤」 の残時間を出す。 */}
      <div className="seat-row seat-row--opp">
        {activeOutcome && <Icon name={outcomeIcon(activeOutcome, 'opp')} size={16} className="seat-outcome" />}
        <span className="seat-name">{oppName ?? '相手'}</span>
        <MatchClock
          kind="seat"
          you={you}
          which="opp"
          frozen={phase !== 'playing'}
          boardId={isWholePolicy ? undefined : activeBoard.board}
          yourSide={slotForBoard(activeBoard.board)}
        />
      </div>

      <section className="play-active">
        {!activeFinished && phase === 'playing' && yourSide && (pendingFromOpponent || pendingFromMe) && (
          <BoardActionsBar
            board={activeBoard.board}
            pendingFromOpponent={pendingFromOpponent}
            pendingFromMe={pendingFromMe}
          />
        )}
        {/* key で active 切替時に再 mount → CSS animation で横スライドイン。
            slideDir は前回 active との比較で 'right'(右から) | 'left'(左から)。
            AnimatedStage が ui-busy counter にアニメ実行中を登録するので、
            CPU の pickMove はスライド完了まで起動を待たされる。 */}
        <AnimatedStage key={activeBoard.board} slideDir={slideDir}>
          <BoardRenderer
            entry={activeBoard}
            legal={legalByBoard[activeBoard.board]}
            yourSide={yourSide}
            myTurnAllowed={myTurnAllowedFor(activeBoard.board)}
            onMove={(mv) => sendMoveGated(activeBoard.board, mv)}
            lastMove={lastMove}
            prevMove={prevMove}
            /* 終局した盤 (投了含む) に「手番待ち」 を出さない。 */
            staticView={activeFinished}
            suppressTurnStatus
          />
        </AnimatedStage>
        {activeBoard.game_id === 'Jungo' && <BoardToolsScene entry={activeBoard} yourSide={yourSide} />}
      </section>

      <div className="seat-row seat-row--you">
        {activeOutcome && <Icon name={outcomeIcon(activeOutcome, 'you')} size={16} className="seat-outcome" />}
        <span className="seat-name">{yourName ?? 'あなた'}</span>
        <BoardTurnStatus entry={activeBoard} mine={myTurnAllowedFor(activeBoard.board)}
          opponent={allowedBoardsFor(snapshot, opponentSeat).includes(activeBoard.board)} spectator={!isPlayer}/>
        <MatchClock
          kind="seat"
          you={you}
          which="mine"
          frozen={phase !== 'playing'}
          boardId={isWholePolicy ? undefined : activeBoard.board}
          yourSide={slotForBoard(activeBoard.board)}
        />
      </div>

      <nav className="play-tabs">
        {tabIndices.map((i) => {
          const b = boards[i];
          const finished = b.result !== null;
          const yourS = sideForBoard(b.board); // 物理色 (盤描画用)
          const yourSlot = slotForBoard(b.board); // slot (勝敗ラベル・clock 用)
          // tab dot は _view.allowed_boards (= 派生計算) を真として扱う。
          const isMine = !finished && myTurnAllowedFor(b.board);
          // 「今は cursor で指せないが、 この盤の cs_seat は自分」 = 自分のラウンドが
          // 回ってきたとき指す予定の盤 (Sequential 待機列の右側)。 isMine と排他で
          // 「赤い太線の中空丸」 を表示する用。
          const isMineSoon = (() => {
            if (finished || isMine || !mySeat || b.current_side == null) return false;
            const fs = b.first_seat ?? 'Leader';
            const csSeat = b.current_side === 'First' ? fs : opponentOf(fs);
            return csSeat === mySeat;
          })();
          // 終局した盤の勝敗は盤名の左の記号 (勝ち = 王冠 / 負け = 白旗 / 引き分け = 天秤)。
          // 文字は読み上げ用。 観戦者は先手/後手のどちらが勝ったかを文字 (先勝/後勝) で出す。
          const resultKind = finished ? tabResultKind(b.result, yourSlot) : null;
          const resultText = finished ? tabResultLabel(b.result, yourSlot) : null;
          return (
            // <button> ではなく <div role="button">: タブ内に BoardThumb (各盤の
            // ミニ render) を入れていて、 mini モードでも cell が <button> なので、
            // <button> の入れ子になって HTML 規格違反になる。 div + role でアクセシビリティ
            // は確保しつつ入れ子を回避。
            <div
              key={b.board}
              role="button"
              tabIndex={0}
              className={`tab ${i === active ? 'active' : ''} ${isMine ? 'mine' : ''} ${
                isMineSoon ? 'mine-soon' : ''
              } ${finished ? 'finished' : ''}`}
              onClick={() => setActiveAnimated(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveAnimated(i);
                }
              }}
              data-testid={`tab-${b.game_id}`}
            >
              <span className="tab-head">
                {/* 手番は色や丸ではなく「濃さ」で示すので、 読み上げ用にだけ状態を書く。 */}
                <span className="sr-only">
                  {isMine
                    ? 'あなたの番'
                    : isMineSoon
                      ? 'もうすぐあなたの番'
                      : finished
                        ? '終局'
                        : '手番待ち'}
                </span>
                <span className="tab-label">
                  {/* 勝敗は slot ベース。 物理色 (yourS) と比べると Renju Swap で逆転する。 */}
                  {resultKind && resultKind !== 'aborted' && (
                    <Icon name={resultKind === 'win' ? 'crown' : resultKind === 'loss' ? 'flag' : 'draw'} size={13} className="tab-outcome" />
                  )}
                  {gameShort(b.game_id)}
                  {resultText && yourSlot == null && ` · ${resultText}`}
                  {resultText && yourSlot != null && <span className="sr-only">{` ${resultText}`}</span>}
                </span>
              </span>
              <div className="tab-board-preview">
                <BoardThumb entry={b} yourSide={yourS} />
              </div>
              {!isWholePolicy && (
                <span className="tab-foot">
                  <MatchClock
                    kind="tab"
                    boardId={b.board}
                    yourSide={yourSlot}
                    frozen={phase !== 'playing'}
                  />
                </span>
              )}
            </div>
          );
        })}
      </nav>

      {/* losingMajority hint は廃止: 対人戦は auto-resign で即終了、 CPU 戦は
          そのまま続行できる通常状態として扱う (= 余計な hint を出さない)。 */}
      {lastError && <p className="error">{lastError}</p>}
    </main>
  );
}

function DrawOfferBanner({
  from,
  you,
  onAccept,
}: {
  from: 'host' | 'guest' | 'spectator';
  you: Side;
  onAccept: () => void;
}) {
  const fromIsYou =
    (from === 'host' && you === 'First') || (from === 'guest' && you === 'Second');
  return (
    <div
      className={`draw-banner ${fromIsYou ? 'draw-banner--mine' : 'draw-banner--theirs'}`}
      data-testid="draw-banner"
      role="status"
    >
      {fromIsYou ? (
        <>
          <span>あなたがドローを申し出ています…</span>
          <small>相手の応答待ち</small>
        </>
      ) : (
        <>
          <span>相手がドローを申し出ました</span>
          <button
            className="btn-secondary draw-banner-accept"
            onClick={onAccept}
            data-testid="accept-draw"
          >
            受諾
          </button>
          <small className="muted">そのまま指せば自動で取り消し</small>
        </>
      )}
    </div>
  );
}

/** Play 中のタブで「Reversi · 勝ち」 のように出す勝敗ラベル。
 *  生の "First"/"Second"/"Draw" を出すと意味不明なので、 自分視点で日本語化する。
 *  - Draw            → 引き分け
 *  - result == 自分Side → 勝ち
 *  - result != 自分Side → 負け
 *  - 観戦者 (yourS=null) は先手/後手の勝ち、 が読めるよう「先勝」/「後勝」 で出す。 */
function tabResultLabel(
  result: 'First' | 'Second' | 'Draw' | null,
  yourS: Side | null,
): string {
  if (result === 'Draw') return '引き分け';
  if (result == null) return '中断';
  if (yourS != null) return result === yourS ? '勝ち' : '負け';
  return result === 'First' ? '先勝' : '後勝';
}

/** 名前の左に出す記号: 勝ち = 王冠、 負け = 白旗、 引き分け = 天秤 (両者)。 */
function outcomeIcon(outcome: 'you' | 'opp' | 'draw', who: 'you' | 'opp'): IconName {
  if (outcome === 'draw') return 'draw';
  return outcome === who ? 'crown' : 'flag';
}

/** 終局した盤の自分視点の勝敗。 観戦者は自分の勝敗が無いので null。 */
function tabResultKind(
  result: 'First' | 'Second' | 'Draw' | null,
  yourS: Side | null,
): 'win' | 'loss' | 'draw' | 'aborted' | null {
  if (result === 'Draw') return 'draw';
  if (result == null) return 'aborted';
  if (yourS == null) return null;
  return result === yourS ? 'win' : 'loss';
}

function BoardThumb({
  entry,
  yourSide,
}: {
  entry: BoardEntry;
  yourSide: Side | null;
}) {
  return <BoardRenderer entry={entry} yourSide={yourSide} mini />;
}

function BoardActionsBar({
  board,
  pendingFromOpponent,
  pendingFromMe,
}: {
  board: number;
  pendingFromOpponent: boolean;
  pendingFromMe: boolean;
}) {
  const sendBoardAction = useRoom((s) => s.sendBoardAction);
  return (
    <div className="board-actions" data-testid={`board-actions-${board}`}>
      {pendingFromOpponent && (
        <div className="board-actions-banner board-actions-banner--theirs" data-testid={`board-draw-offer-${board}`}>
          <span>相手から引き分け提案</span>
          <button
            className="btn-primary"
            onClick={() => sendBoardAction(board, 'AcceptDraw')}
            data-testid={`board-accept-draw-${board}`}
          >
            受諾
          </button>
          <button
            className="btn-secondary"
            onClick={() => sendBoardAction(board, 'DeclineDraw')}
            data-testid={`board-decline-draw-${board}`}
          >
            拒否
          </button>
        </div>
      )}
      {pendingFromMe && (
        <div className="board-actions-banner board-actions-banner--mine">
          <span>引き分け提案中…</span>
        </div>
      )}
    </div>
  );
}

/** 盤切替時の横スライドイン アニメ用ステージ。 ref に animationstart/end を購読して
 *  ui-busy counter に反映する。 親が key 切替で再 mount するので、 useRef が新規になり
 *  listener も新しい DOM ノードに正しく付け直される。 */
function AnimatedStage({ slideDir, children }: { slideDir: 'right' | 'left' | null; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useUiBusyAnim(ref);
  return (
    <div ref={ref} className={`play-active-stage ${slideDir ? `slide-in-${slideDir}` : ''}`}>
      {children}
    </div>
  );
}
